import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { request } from "app/network";
import Swal from "sweetalert2";

interface ReorderAdModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    ads: Array<{ seqId: number; title: string; displayOrder: number }>;
    darkMode?: boolean;
}

interface SortableAdItemProps {
    id: string;
    title: string;
    darkMode: boolean;
}

function SortableAdItem({ id, title, darkMode }: SortableAdItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                border: darkMode
                    ? "1px solid rgba(255, 255, 255, 0.2)"
                    : "1px solid rgba(0, 0, 0, 0.2)",
                backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)",
                cursor: "grab",
                "&:active": {
                    cursor: "grabbing",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                }}
            />
            <MDTypography
                variant="button"
                fontWeight="regular"
                sx={{
                    color: darkMode
                        ? "rgba(255, 255, 255, 0.8)"
                        : "#344767",
                }}
            >
                {title}
            </MDTypography>
        </MDBox>
    );
}

function ReorderAdModal({ open, onClose, onSuccess, ads, darkMode = false }: ReorderAdModalProps) {
    const [orderedAds, setOrderedAds] = useState(
        ads.sort((a, b) => a.displayOrder - b.displayOrder)
    );
    const [loading, setLoading] = useState(false);
    const [isRandom, setIsRandom] = useState(false);

    // Fetch config on open
    React.useEffect(() => {
        if (open) {
            setLoading(true);
            request("SystemConfig.GetConfig", { paramz: { Key: "INTERSTITIAL_AD_RANDOM_ORDER" } })
                .then((res: any) => {
                    setIsRandom(res.data.value === "true");
                })
                .catch((err: any) => console.error("Config Fetch Error", err))
                .finally(() => setLoading(false));
        }
    }, [open]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setOrderedAds((items) => {
                const oldIndex = items.findIndex((item) => `ad-${item.seqId}` === active.id);
                const newIndex = items.findIndex((item) => `ad-${item.seqId}` === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Save Random Setting
            await request("SystemConfig.SetConfig", {
                paramz: {
                    Key: "INTERSTITIAL_AD_RANDOM_ORDER",
                    Value: isRandom ? "true" : "false",
                    Description: "Interstitial Ad Random Order Mode"
                }
            });

            if (!isRandom) {
                // Save Order only if not random
                const updates = orderedAds.map((ad, index) => ({
                    seqId: ad.seqId,
                    displayOrder: index + 1,
                }));

                for (const update of updates) {
                    await request("InterstitialAd.updateDisplayOrder", {
                        paramz: {
                            SeqId: update.seqId,
                            DisplayOrder: update.displayOrder,
                        },
                    });
                }
            }

            Swal.fire({
                title: "성공",
                text: "광고 순서가 변경되었습니다.",
                icon: "success",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("광고 순서 변경 실패:", error);
            Swal.fire({
                title: "오류",
                text: error.message || "광고 순서 변경에 실패했습니다.",
                icon: "error",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset ordered ads when modal opens
    React.useEffect(() => {
        if (open) {
            setOrderedAds(ads.sort((a, b) => a.displayOrder - b.displayOrder));
        }
    }, [open, ads]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? "#1a2035" : "#fff",
                    color: darkMode ? "#fff" : "#000",
                },
            }}
        >
            <DialogTitle>광고 순서 변경</DialogTitle>
            <DialogContent>
                <MDBox pt={2}>
                    <MDTypography
                        variant="caption"
                        fontWeight="regular"
                        sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }}
                        mb={2}
                        display="block"
                    >
                        드래그하여 광고 순서를 변경하세요
                    </MDTypography>

                    <MDBox mb={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isRandom}
                                    onChange={(e) => setIsRandom(e.target.checked)}
                                    color="info"
                                />
                            }
                            label={
                                <MDTypography variant="button" color={darkMode ? "white" : "text"}>
                                    랜덤 노출 (사용 시 순서 무시)
                                </MDTypography>
                            }
                        />
                    </MDBox>

                    {!isRandom && (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={orderedAds.map((ad) => `ad-${ad.seqId}`)}
                                strategy={verticalListSortingStrategy}
                            >
                                {orderedAds.map((ad) => (
                                    <SortableAdItem
                                        key={ad.seqId}
                                        id={`ad-${ad.seqId}`}
                                        title={ad.title}
                                        darkMode={darkMode}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                    {isRandom && (
                        <MDBox p={2} sx={{ opacity: 0.5, pointerEvents: "none" }}>
                            {orderedAds.map((ad) => (
                                <SortableAdItem
                                    key={ad.seqId}
                                    id={`ad-${ad.seqId}`}
                                    title={ad.title}
                                    darkMode={darkMode}
                                />
                            ))}
                        </MDBox>
                    )}
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="outlined" color="secondary">
                    취소
                </MDButton>
                <MDButton
                    onClick={handleSave}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? "저장 중..." : "저장"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

export default ReorderAdModal;
